// lib/supabase.ts
import { createClient } from "@supabase/supabase-js";
import { invalidateByPrefix, readCache, writeCache } from "./cache";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_KEY as string;

const CACHEABLE_PATHS = new Set([
  "/rest/v1/practitioner_keys",
  "/rest/v1/profiles",
  "/rest/v1/clinic_practitioners",
  "/rest/v1/clients",
]);

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 10 minutes
const CACHE_PREFIX = "supabase-cache:";

function getHeader(headers: HeadersInit | undefined, name: string) {
  if (!headers) return undefined;
  if (headers instanceof Headers) return headers.get(name);
  if (Array.isArray(headers)) {
    const found = headers.find(([key]) => key.toLowerCase() === name.toLowerCase());
    return found ? found[1] : undefined;
  }
  const record = headers as Record<string, string>;
  return record[name] ?? record[name.toLowerCase()];
}

function isCacheable(request: Request) {
  if (request.method !== "GET") return false;
  try {
    const url = new URL(request.url);
    if (!CACHEABLE_PATHS.has(url.pathname)) return false;
    // Only cache SELECT queries (avoid HEAD or RPC etc.)
    return url.searchParams.has("select");
  } catch (e) {
    console.warn("[supabase-cache] Failed to parse URL", e);
    return false;
  }
}

function buildCacheKey(request: Request) {
  const auth = getHeader(request.headers, "Authorization") || "";
  return `${CACHE_PREFIX}${request.url}|${auth}`;
}

async function cachedFetch(input: RequestInfo | URL, init?: RequestInit) {
  const request = new Request(input, init);

  if (!isCacheable(request)) {
    return fetch(request);
  }

  const cacheKey = buildCacheKey(request);
  const cached = readCache<{ body: string; status: number; headers: Record<string, string> }>(cacheKey);
  if (cached) {
    return new Response(cached.body, {
      status: cached.status,
      headers: cached.headers,
    });
  }

  const response = await fetch(request);
  if (!response.ok) return response;

  const clone = response.clone();
  const body = await clone.text();
  const headers: Record<string, string> = {};
  clone.headers.forEach((value, key) => {
    headers[key] = value;
  });

  writeCache(cacheKey, { body, status: response.status, headers }, CACHE_TTL_MS);
  return response;
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
    storageKey: "connectlah-session",
  },
  global: {
    fetch: cachedFetch,
  },
});

/** Returns "Bearer <token>" for authenticated backend calls, or null if no session. */
export async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? `Bearer ${session.access_token}` : null;
}

// ✅ Nettoie le cache quand on change d'utilisateur
const logoutEvents = ["SIGNED_OUT"] as const;

supabase.auth.onAuthStateChange((event) => {
  if (logoutEvents.includes(event as typeof logoutEvents[number])) {
    invalidateByPrefix(CACHE_PREFIX);
    localStorage.removeItem("decryptCache");
  }
});
